import { useEffect, useState } from "react";
import { useLocation, useParams } from "wouter";
import { useForm } from "react-hook-form";
import api from "@/lib/api";
import { ImageUpload } from "@/components/admin/ImageUpload";
import { ArrowLeft } from "lucide-react";
import { Link } from "wouter";

interface FormData {
  name: string;
  logo: string;
  svgCode: string;
  svgColor: string;
  url: string;
  sortOrder: number;
}

function SvgPreview({ svgCode, svgColor }: { svgCode: string; svgColor: string }) {
  if (!svgCode) return null;

  // Replace all fill attributes with the chosen color
  const coloredSvg = svgCode
    .replace(/fill="[^"]*"/g, `fill="${svgColor}"`)
    .replace(/fill\s*:\s*#[0-9a-fA-F]{3,8}/g, `fill:${svgColor}`)
    .replace(/fill\s*:\s*rgb[^;)]+[;)]/g, `fill:${svgColor};`)
    .replace(/<svg([^>]*)>/, (_match, attrs) => {
      const attrStr = attrs as string;
      const wMatch = attrStr.match(/\bwidth\s*=\s*"([^"]*)"/);
      const hMatch = attrStr.match(/\bheight\s*=\s*"([^"]*)"/);
      const hasViewBox = /\bviewBox\s*=/.test(attrStr);
      let newAttrs = attrStr
        .replace(/\bwidth\s*=\s*"[^"]*"/g, "")
        .replace(/\bheight\s*=\s*"[^"]*"/g, "");
      if (!hasViewBox && wMatch && hMatch) {
        newAttrs += ` viewBox="0 0 ${wMatch[1]} ${hMatch[1]}"`;
      }
      return `<svg${newAttrs} fill="${svgColor}" style="height:80px;width:auto">`;
    });

  return (
    <div className="mt-3 p-4 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center min-h-[100px]">
      <div dangerouslySetInnerHTML={{ __html: coloredSvg }} />
    </div>
  );
}

export default function ClientForm() {
  const params = useParams<{ id: string }>();
  const isEdit = params.id && params.id !== "new";
  const [, navigate] = useLocation();
  const [saving, setSaving] = useState(false);
  const [logoMode, setLogoMode] = useState<"image" | "svg">("image");

  const { register, handleSubmit, setValue, watch, reset } = useForm<FormData>({
    defaultValues: { name: "", logo: "", svgCode: "", svgColor: "#ffffff", url: "", sortOrder: 0 },
  });

  const logoValue = watch("logo");
  const svgCodeValue = watch("svgCode");
  const svgColorValue = watch("svgColor");

  useEffect(() => {
    if (isEdit) {
      api.get(`/clients`).then(({ data }) => {
        const client = data.find((c: any) => c.id === parseInt(params.id!));
        if (client) {
          reset(client);
          if (client.svgCode) setLogoMode("svg");
        }
      });
    }
  }, [isEdit, params.id, reset]);

  const onSubmit = async (data: FormData) => {
    setSaving(true);
    try {
      // Clear the unused mode's data
      const payload = { ...data };
      if (logoMode === "svg") {
        payload.logo = "";
      } else {
        payload.svgCode = "";
        payload.svgColor = "#ffffff";
      }

      if (isEdit) {
        await api.put(`/clients/${params.id}`, payload);
      } else {
        await api.post("/clients", payload);
      }
      navigate("/admin/clients");
    } catch (err: any) {
      alert(err.response?.data?.error || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Link href="/admin/clients" className="text-white/40 hover:text-white transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <h1 className="text-2xl font-bold text-white font-[Rubik]">
          {isEdit ? "Edit Client" : "New Client"}
        </h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="max-w-xl space-y-5">
        <div>
          <label className="block text-sm text-white/60 mb-1.5">Name</label>
          <input
            {...register("name")}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#66CDAA]/50 outline-none"
            required
          />
        </div>

        {/* Logo mode toggle */}
        <div>
          <label className="block text-sm text-white/60 mb-2">Logo</label>
          <div className="flex gap-2 mb-3">
            <button
              type="button"
              onClick={() => setLogoMode("image")}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                logoMode === "image"
                  ? "bg-[#66CDAA] text-[#0F172A]"
                  : "bg-white/5 text-white/60 hover:text-white"
              }`}
            >
              Upload Image
            </button>
            <button
              type="button"
              onClick={() => setLogoMode("svg")}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                logoMode === "svg"
                  ? "bg-[#66CDAA] text-[#0F172A]"
                  : "bg-white/5 text-white/60 hover:text-white"
              }`}
            >
              Paste SVG
            </button>
          </div>

          {logoMode === "image" ? (
            <ImageUpload value={logoValue} onChange={(url) => setValue("logo", url)} />
          ) : (
            <div className="space-y-3">
              <textarea
                {...register("svgCode")}
                placeholder="Paste SVG code here..."
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm font-mono focus:border-[#66CDAA]/50 outline-none resize-none h-32"
                dir="ltr"
              />
              <div className="flex items-center gap-3">
                <label className="text-sm text-white/60">Color:</label>
                <input
                  type="color"
                  {...register("svgColor")}
                  className="w-10 h-10 rounded-lg border border-white/10 bg-transparent cursor-pointer"
                />
                <input
                  {...register("svgColor")}
                  className="w-28 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-mono focus:border-[#66CDAA]/50 outline-none"
                  dir="ltr"
                />
                <button
                  type="button"
                  onClick={() => setValue("svgColor", "#ffffff")}
                  className="px-3 py-2 text-xs rounded-lg bg-white/5 text-white/60 hover:text-white transition-colors"
                >
                  White
                </button>
                <button
                  type="button"
                  onClick={() => setValue("svgColor", "#66CDAA")}
                  className="px-3 py-2 text-xs rounded-lg bg-white/5 text-white/60 hover:text-white transition-colors"
                >
                  Brand
                </button>
              </div>
              <SvgPreview svgCode={svgCodeValue} svgColor={svgColorValue} />
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-white/60 mb-1.5">URL</label>
            <input
              {...register("url")}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#66CDAA]/50 outline-none"
              dir="ltr"
            />
          </div>
          <div>
            <label className="block text-sm text-white/60 mb-1.5">Sort order</label>
            <input
              type="number"
              {...register("sortOrder", { valueAsNumber: true })}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#66CDAA]/50 outline-none"
            />
          </div>
        </div>

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={saving}
            className="bg-[#66CDAA] hover:bg-[#57b899] text-[#0F172A] font-bold px-6 py-3 rounded-xl transition-colors disabled:opacity-50"
          >
            {saving ? "Saving..." : isEdit ? "Update" : "Create"}
          </button>
          <Link
            href="/admin/clients"
            className="px-6 py-3 rounded-xl border border-white/10 text-white/50 hover:text-white hover:border-white/20 transition-colors"
          >
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}
